// src/components/Privacy.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Introduction</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Epiphoric ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                By using Epiphoric, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.1 Account Information</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Email address (required for authentication)</li>
                <li>Display name (optional, from Google OAuth or manually entered)</li>
                <li>Profile picture URL (optional, from Google OAuth)</li>
                <li>Authentication tokens (managed by Firebase Authentication)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.2 Idea Data</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We collect and store the following information about your ideas:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li><strong>Encrypted Data:</strong> Idea titles, descriptions, status, tags, and TODO items are encrypted using AES encryption before storage</li>
                <li><strong>Plain Text Data:</strong> Lessons learned and resource links are stored as plain text to enable search and sharing functionality</li>
                <li>Ratings, timestamps, and metadata</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2.3 Usage Data</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may collect information about how you access and use the application, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>IP address (for security and analytics)</li>
                <li>Usage patterns and feature interactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>To provide, maintain, and improve our services</li>
                <li>To authenticate your identity and secure your account</li>
                <li>To store and retrieve your encrypted idea data</li>
                <li>To enable features such as tags, resources, and TODO management</li>
                <li>To analyze usage patterns and improve user experience</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Data Security</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.1 Encryption</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li><strong>AES Encryption:</strong> All sensitive idea data (titles, descriptions, status, tags, TODOs) is encrypted using AES-256 encryption before being stored in our database</li>
                <li><strong>Client-Side Encryption:</strong> Encryption and decryption occur in your browser using a key stored in environment variables</li>
                <li><strong>Secure Transmission:</strong> All data is transmitted over HTTPS/TLS</li>
                <li><strong>Authentication:</strong> Firebase Authentication provides secure user authentication with industry-standard protocols</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.2 Data Isolation</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Your data is isolated by user ID in our database. Each user can only access their own encrypted data, and we cannot decrypt your ideas without your encryption key.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.3 Limitations</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                While we implement strong security measures, please note:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Lessons learned and resource links are stored as plain text (by design, for searchability)</li>
                <li>No method of transmission over the Internet or electronic storage is 100% secure</li>
                <li>You are responsible for keeping your encryption key secure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Data Storage and Retention</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.1 Storage Location</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Your data is stored in Google Cloud Firestore, which provides:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Geographic data residency options</li>
                <li>Automatic backups and redundancy</li>
                <li>Compliance with industry security standards</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.2 Data Retention</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We retain your data for as long as your account is active. You can:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Delete individual ideas at any time</li>
                <li>Delete your account, which will remove all associated data</li>
                <li>Export your data before deletion (contact support for assistance)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Third-Party Services</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use the following third-party services:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li><strong>Firebase (Google):</strong> Authentication, database storage, and hosting services. See <a href="https://firebase.google.com/support/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
                <li><strong>Google OAuth:</strong> For social authentication (if you choose to sign in with Google)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Your Rights</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and all associated data</li>
                <li>Export your data</li>
                <li>Opt out of certain data collection (where applicable)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                To exercise these rights, please contact us or use the account deletion feature in your profile settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700 dark:text-gray-300">
                Epiphoric is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 dark:text-gray-300">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Contact Us</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have any questions about this Privacy Policy, please contact us through our support channel:
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <a 
                  href="https://github.com/Leftyshields/epiphoric/issues" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  GitHub Issues
                </a>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                All support requests are handled through GitHub Issues. Please open an issue for privacy-related questions or concerns.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;

